import LeBell from "@dsk/icons/LeBell.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";

import LeSearchChangedLanguageSelectorToast from "@/components/toasts/LeSearchChangedLanguageSelectorToast.vue";
import {
  DictionariesHelper,
  DictionaryGroupsHelper,
  EntitiesHelper,
  MatomoHelper,
  SearchFilterHelper,
  SearchRouteHelper
} from "@/helpers";
import {
  useDictionariesStore,
  useDictionaryGroupsStore,
  useSearchFilterStore,
  useSearchStore,
  useUserStore
} from "@/stores";
import { type DictionaryGroupWithDictionaries, MatomoEventAction, MatomoEventCategory } from "@/types";

export function useDictionarySelector() {
  const i18n = useI18n();
  const toast = useToast();

  const dictionariesStore = useDictionariesStore();
  const dictionaryGroupsStore = useDictionaryGroupsStore();
  const searchFilterStore = useSearchFilterStore();
  const searchStore = useSearchStore();

  const searchRouteHelper = new SearchRouteHelper(useRouter(), useUserStore().userData);

  const companyDictionaryIds = computed<string[]>(() => EntitiesHelper.getIds(dictionariesStore.companyDictionaries));

  const groupsWithDictionaries = computed<DictionaryGroupWithDictionaries[]>(() => {
    const sortedGroups = DictionaryGroupsHelper.sortByTagsPriority(dictionaryGroupsStore.groups);
    const sortedDictionaries = DictionariesHelper.sortByPublisherPriority(dictionariesStore.dictionaries);

    const dictionariesWithoutCompany = sortedDictionaries.filter(
      (dictionary) => !companyDictionaryIds.value.includes(dictionary.id)
    );

    return DictionaryGroupsHelper.addDictionariesToGroups(sortedGroups, dictionariesWithoutCompany);
  });

  const showChangedLanguagePairToast = () => {
    toast.clear();
    toast(
      {
        component: LeSearchChangedLanguageSelectorToast,
        props: { i18n, type: "changed-language-pair" }
      },
      { timeout: 20000, icon: LeBell }
    );

    MatomoHelper.sendEvent(
      MatomoEventCategory.Search,
      MatomoEventAction.ToastDisplayed,
      "Change of language selector toast displayed"
    );
  };

  const onSearchInDictionary = (dictionaryId: string) => {
    const dictionary = dictionariesStore.getDictionaryById(dictionaryId);

    // Somehow unable to find the dictionary, so we attempt to redirect to it
    if (!dictionary) {
      return searchRouteHelper.selectDictionary(dictionaryId);
    }

    // Selected dictionary is still within the search filter, so simply select it
    if (SearchFilterHelper.dictionaryIsCompatibleWithSearchFilter(dictionary, searchFilterStore.filter)) {
      return searchRouteHelper.selectDictionary(dictionaryId);
    }

    searchFilterStore.filter = SearchFilterHelper.convertToSearchFilter(dictionary);
    searchRouteHelper.searchWithChangedSearchFilter(searchStore.query, searchFilterStore.filter, dictionaryId);

    showChangedLanguagePairToast();
  };

  const onSelectDictionary = (dictionaryId: string) => {
    const dictionary = dictionariesStore.getDictionaryById(dictionaryId);

    if (!dictionary) {
      searchStore.selectedDictionaryId = undefined;
      return;
    }

    if (SearchFilterHelper.dictionaryIsCompatibleWithSearchFilter(dictionary, searchFilterStore.filter)) {
      searchStore.selectedDictionaryId = dictionaryId;
      return;
    }

    searchFilterStore.filter = SearchFilterHelper.convertToSearchFilter(dictionary);
    searchStore.selectedDictionaryId = dictionaryId;

    showChangedLanguagePairToast();
  };

  return {
    groupsWithDictionaries,
    onSearchInDictionary,
    onSelectDictionary
  };
}
